/**
 * Per-screen "+ New" registration.
 *
 * The desktop topbar's ``+ New`` button reads the currently-registered
 * handler from this context and hides itself when none is registered.
 * Each screen calls ``useRegisterNewAction(...)`` on mount with its
 * own creator (e.g. Notes registers "new note", Calendar "new event").
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

interface NewAction {
  /** Short label for the button (e.g. "New note"). */
  label: string;
  handler: () => void;
}

interface NewActionContextValue {
  current: NewAction | null;
  register(action: NewAction): () => void;
}

const Ctx = createContext<NewActionContextValue | null>(null);

export function NewActionProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [current, setCurrent] = useState<NewAction | null>(null);

  const register = useCallback((action: NewAction): (() => void) => {
    setCurrent(action);
    return () => setCurrent((c) => (c === action ? null : c));
  }, []);

  const value = useMemo<NewActionContextValue>(
    () => ({ current, register }),
    [current, register],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCurrentNewAction(): NewAction | null {
  const ctx = useContext(Ctx);
  return ctx?.current ?? null;
}

/**
 * Register a "+ New" action for the lifetime of the calling component.
 * Auto-cleans up on unmount.
 */
export function useRegisterNewAction(
  label: string,
  handler: () => void,
): void {
  const ctx = useContext(Ctx);
  useEffect(() => {
    if (!ctx) return;
    return ctx.register({ label, handler });
  }, [ctx, label, handler]);
}
